export type ArrGapTrack = "Leader" | "Senior PM";

export type HirerRole =
  | "founder-ceo"
  | "product-leader"
  | "investor-talent-leader";

export type ArrGapAnswer = {
  label: string;
  score: 1 | 2 | 3 | 4;
};

export type ArrGapQuestionId =
  | "decision-rights"
  | "mandate"
  | "commercial-ownership"
  | "customer-evidence"
  | "ai"
  | "hiring-process";

export type ArrGapQuestion = {
  id: ArrGapQuestionId;
  label: string;
  prompt: string;
  answers: ArrGapAnswer[];
  riskCopy: string;
};

export type ArrGapContextQuestionId =
  | "hirerRole"
  | "hiringFor"
  | "questionTrack"
  | "seniorPmOwnership"
  | "arrBand"
  | "stage"
  | "headcount"
  | "productTeamSize"
  | "whoOwnsProduct"
  | "growthGoal"
  | "mainLever"
  | "hiringSituation"
  | "lean"
  | "hardestPart"
  | "budgetFlexibility"
  | "unusualConfirmed";

export type ArrGapContextQuestion = {
  id: ArrGapContextQuestionId;
  label: string;
  options: { label: string; value: string }[];
};

const leaderQuestions: ArrGapQuestion[] = [
  {
    id: "decision-rights",
    label: "Decision rights",
    prompt: "Imagine your new product leader starts Monday. Which of these would be true a month in?",
    answers: [
      { label: "I’m still signing off the roadmap. They run the meetings.", score: 1 },
      { label: "They own the backlog and sequencing. I still decide anything touching pricing, big customers or next quarter’s bets.", score: 3 },
      { label: "I’ve handed over some decisions, but I haven’t told the team which ones. I step in when I disagree.", score: 2 },
      { label: "I’ve named three decisions I no longer make, written them down, and told the team and the board.", score: 4 },
    ],
    riskCopy: "You're hiring someone to own decisions you haven't let go of. That's the most common reason these hires stall.",
  },
  {
    id: "mandate",
    label: "Mandate",
    prompt: "Ask your CEO, CTO and lead investor separately what this hire must change in 12 months. What do you get?",
    answers: [
      { label: "I’ve never asked. I assume we agree.", score: 1 },
      { label: "The same words, different meanings. “More strategic” and “faster” mean different things to each of us.", score: 2 },
      { label: "We agree on the problem, but it isn’t written down anywhere.", score: 3 },
      { label: "It’s written down with a measure, and all three of us have signed it off.", score: 4 },
    ],
    riskCopy: "Ask three people what this hire is for and you'll get three answers. Candidates will hear all of them.",
  },
  {
    id: "commercial-ownership",
    label: "Commercial ownership",
    prompt: "Who in product can tell you what they did last quarter to move NRR, activation or win rate?",
    answers: [
      { label: "Nobody. Product is judged on what ships.", score: 1 },
      { label: "Someone could guess, but the number belongs to sales or customer success.", score: 2 },
      { label: "Product tracks it, but it isn’t in anyone’s goals or reviews.", score: 3 },
      { label: "Named people own a number, it’s in their goals, and it’s reviewed monthly.", score: 4 },
    ],
    riskCopy: "Product isn't connected to a revenue number, so a new leader has nothing to be measured on and nothing to move.",
  },
  {
    id: "customer-evidence",
    label: "Customer evidence",
    prompt: "Think of the last time the roadmap changed. What triggered it?",
    answers: [
      { label: "A big customer or a sales deal asked for it.", score: 2 },
      { label: "An exec, probably me, felt strongly about it.", score: 1 },
      { label: "A mix of customer feedback and exec opinion. Hard to separate now.", score: 3 },
      { label: "Customer conversations and data the team brought, and we can show the trail.", score: 4 },
    ],
    riskCopy: "Your roadmap follows the loudest voice. A strong product leader will either fight that or leave.",
  },
  {
    id: "ai",
    label: "AI",
    prompt: "Your board asks what the AI plan is. What do you actually say?",
    answers: [
      { label: "We’re looking at it. Nobody owns it.", score: 1 },
      { label: "We’ve shipped something, but we haven’t checked whether it earns back its cost.", score: 2 },
      { label: "We have a view and an owner, but the margin impact is a guess.", score: 3 },
      { label: "It’s a decision: feature, margin lever or the product. Named owner, and cost and quality are tracked.", score: 4 },
    ],
    riskCopy: "Nobody can say what AI is for in your business, which means you can't say what you're hiring someone to do about it.",
  },
  {
    id: "hiring-process",
    label: "Hiring process",
    prompt: "Think of your last senior hire. How did you decide?",
    answers: [
      { label: "Gut feel after a good conversation.", score: 1 },
      { label: "Several interviews, each person asked their own questions, then we talked it over.", score: 2 },
      { label: "We had criteria, but they shifted as we met candidates.", score: 3 },
      { label: "Agreed scorecard before the first interview, interviewers scored separately, then compared.", score: 4 },
    ],
    riskCopy: "You're about to make a high-stakes hire on a process that can't tell a good answer from a good talker.",
  },
];

export const arrGapQuestions = leaderQuestions;

const seniorPmQuestions: ArrGapQuestion[] = [
  {
    id: "decision-rights",
    label: "Decision rights",
    prompt: "Imagine your new senior PM starts Monday. Which of these would be true a month in?",
    answers: [
      { label: "I'm still deciding what they work on and signing off their specs.", score: 1 },
      { label: "They own discovery and delivery for their area. I still decide anything touching pricing, big customers or next quarter's bets.", score: 3 },
      { label: "They have real ownership, but the team isn't clear where it stops and mine starts.", score: 2 },
      { label: "Their area and the decisions they own are written down, and the team knows it.", score: 4 },
    ],
    riskCopy: "You're hiring a senior PM to own an area you haven't defined. That's how good people end up managing a backlog.",
  },
  {
    id: "mandate",
    label: "Mandate",
    prompt: "Ask the hiring manager, the engineering lead and the design lead separately what this hire must change in six months. What do you get?",
    answers: [
      { label: "I've never asked. I assume we agree.", score: 1 },
      { label: "Three different answers. One wants speed, one wants quality, one wants strategy.", score: 2 },
      { label: "We agree on the problem, but it isn't written down anywhere.", score: 3 },
      { label: "It's written down with a measure, and all three have signed it off.", score: 4 },
    ],
    riskCopy: "Ask three people what this hire is for and you'll get three answers. Candidates will hear all of them.",
  },
  {
    id: "commercial-ownership",
    label: "Commercial ownership",
    prompt: "What number will this person own?",
    answers: [
      { label: "No number. They'll own a backlog.", score: 1 },
      { label: "A number exists, but sales or customer success owns it.", score: 2 },
      { label: "We have a number in mind, but it isn't in their goals yet.", score: 3 },
      { label: "A named metric with an agreed baseline, in their goals and reviewed monthly.", score: 4 },
    ],
    riskCopy: "Nobody has named the number this hire owns, so they'll be judged on what ships.",
  },
  {
    id: "customer-evidence",
    label: "Customer evidence",
    prompt: "Think of the last time the roadmap changed. What triggered it?",
    answers: [
      { label: "A big customer or a sales deal asked for it.", score: 2 },
      { label: "An exec, probably me, felt strongly about it.", score: 1 },
      { label: "A mix of customer feedback and exec opinion. Hard to separate now.", score: 3 },
      { label: "Customer conversations and data the team brought, and we can show the trail.", score: 4 },
    ],
    riskCopy: "Your roadmap follows the loudest voice. A strong product manager will either fight that or leave.",
  },
  {
    id: "ai",
    label: "AI",
    prompt: "Your engineers can now build in days what took weeks. What's true about how your PMs work today?",
    answers: [
      { label: "Engineering ships faster, but nothing has changed in how product decides what to build.", score: 1 },
      { label: "Some PMs prototype with AI tools, informally. There are no shared standards.", score: 2 },
      { label: "We've agreed where AI can help, but how we judge quality and cost is still a guess.", score: 3 },
      { label: "We have clear expectations for PMs using AI, including evals, quality bars and cost, and we hire against them.", score: 4 },
    ],
    riskCopy: "You haven't said what you expect from a PM in an AI-assisted team, so you can't screen for it.",
  },
  {
    id: "hiring-process",
    label: "Hiring process",
    prompt: "Think of your last senior PM hire. How did you decide?",
    answers: [
      { label: "Gut feel after a good conversation.", score: 1 },
      { label: "Several interviews, each person asked their own questions, then we talked it over.", score: 2 },
      { label: "We had criteria, but they shifted as we met candidates, and the exercise wasn't scored consistently.", score: 3 },
      { label: "Agreed scorecard before the first interview, a scored exercise, interviewers scored separately, then compared.", score: 4 },
    ],
    riskCopy: "You're about to make a senior hire on a process that can't tell a strong PM from a polished interviewer.",
  },
];

const investorQuestionCopy: Record<ArrGapTrack, Record<ArrGapQuestionId, { prompt: string; answers: string[] }>> = {
  Leader: {
    "decision-rights": {
      prompt: "Imagine the company's new product leader starts Monday. Which of these would be true a month in?",
      answers: [
        "The CEO is still signing off the roadmap. The new leader runs the meetings.",
        "They own the backlog and sequencing. The CEO still decides anything touching pricing, big customers or next quarter’s bets.",
        "The CEO has handed over some decisions, but the company has not been told which ones. The CEO steps in when they disagree.",
        "The CEO has named three decisions they no longer make, written them down, and told the company and the board.",
      ],
    },
    mandate: {
      prompt: "The CEO, the CTO and the lead investor are asked separately what this hire must change in 12 months. What do they say?",
      answers: [
        "The CEO has never asked. They assume everyone agrees.",
        "The same words, different meanings. “More strategic” and “faster” mean different things to each person.",
        "The leadership team agrees on the problem, but it is not written down anywhere.",
        "It is written down with a measure, and the CEO, CTO and lead investor have signed it off.",
      ],
    },
    "commercial-ownership": {
      prompt: "Who in the company’s product team can say what they did last quarter to move NRR, activation or win rate?",
      answers: [
        "Nobody. The product team is judged on what ships.",
        "Someone could guess, but the number belongs to sales or customer success.",
        "The product team tracks it, but it is not in anyone’s goals or reviews.",
        "Named people own a number, it is in their goals, and it is reviewed monthly.",
      ],
    },
    "customer-evidence": {
      prompt: "Think of the last time the company’s roadmap changed. What triggered it?",
      answers: [
        "A big customer or a sales deal asked for it.",
        "An executive, probably the CEO, felt strongly about it.",
        "A mix of customer feedback and executive opinion. Hard to separate now.",
        "Customer conversations and data the team brought, and the company can show the trail.",
      ],
    },
    ai: {
      prompt: "The board asks what the AI plan is. What does the CEO actually say?",
      answers: [
        "The company is looking at it. Nobody owns it.",
        "The company has shipped something, but no one has checked whether it earns back its cost.",
        "The company has a view and an owner, but the margin impact is a guess.",
        "It is a decision: feature, margin lever or the product. There is a named owner, and cost and quality are tracked.",
      ],
    },
    "hiring-process": {
      prompt: "Think of the company’s last senior hire. How was it decided?",
      answers: [
        "Gut feel after a good conversation.",
        "Several interviews, each person asked their own questions, then the leadership team talked it over.",
        "The company had criteria, but they shifted as candidates were met.",
        "An agreed scorecard was in place before the first interview. Interviewers scored separately, then compared.",
      ],
    },
  },
  "Senior PM": {
    "decision-rights": {
      prompt: "Imagine the company’s new senior PM starts Monday. Which of these would be true a month in?",
      answers: [
        "The hiring manager is still deciding what the PM works on and signing off their specs.",
        "They own discovery and delivery for their area. The hiring manager still decides anything touching pricing, big customers or next quarter’s bets.",
        "They have real ownership, but the company is not clear where it stops and the hiring manager’s starts.",
        "Their area and the decisions they own are written down, and the company knows it.",
      ],
    },
    mandate: {
      prompt: "The hiring manager, the engineering lead and the design lead are asked separately what this hire must change in six months. What do they say?",
      answers: [
        "The hiring manager has never asked. They assume everyone agrees.",
        "Three different answers. One wants speed, one wants quality, one wants strategy.",
        "The leadership team agrees on the problem, but it is not written down anywhere.",
        "It is written down with a measure, and all three have signed it off.",
      ],
    },
    "commercial-ownership": {
      prompt: "What number will this person own at the company?",
      answers: [
        "No number. They will own a backlog.",
        "A number exists, but sales or customer success owns it.",
        "The leadership team has a number in mind, but it is not in their goals yet.",
        "A named metric with an agreed baseline, in their goals and reviewed monthly.",
      ],
    },
    "customer-evidence": {
      prompt: "Think of the last time the company’s roadmap changed. What triggered it?",
      answers: [
        "A big customer or a sales deal asked for it.",
        "An executive, probably the CEO, felt strongly about it.",
        "A mix of customer feedback and executive opinion. Hard to separate now.",
        "Customer conversations and data the team brought, and the company can show the trail.",
      ],
    },
    ai: {
      prompt: "The company’s engineers can now build in days what took weeks. What is true about how the company’s PMs work today?",
      answers: [
        "The engineering team ships faster, but nothing has changed in how product decides what to build.",
        "Some PMs prototype with AI tools, informally. There are no shared standards.",
        "The company has agreed where AI can help, but how it judges quality and cost is still a guess.",
        "The company has clear expectations for PMs using AI, including evals, quality bars and cost, and hires against them.",
      ],
    },
    "hiring-process": {
      prompt: "Think of the company’s last senior PM hire. How was it decided?",
      answers: [
        "Gut feel after a good conversation.",
        "Several interviews, each person asked their own questions, then the leadership team talked it over.",
        "The company had criteria, but they shifted as candidates were met, and the exercise was not scored consistently.",
        "An agreed scorecard was in place before the first interview, with a scored exercise. Interviewers scored separately, then compared.",
      ],
    },
  },
};

function leaderMandateQuestion(hirerRole?: string) {
  if (hirerRole === "founder-ceo") {
    return {
      prompt: "Ask your CTO, your lead investor and one other senior leader separately what this hire must change in 12 months. What do you get?",
      fourthAnswer: "It's written down with a measure, and everyone I asked has signed it off.",
    };
  }
  if (hirerRole === "product-leader") return "Ask your CEO, your CTO and your senior commercial leader separately what this hire must change in 12 months. What do you get?";
  if (hirerRole === "investor-talent-leader") return "Ask the CEO, the CTO and the lead investor separately what this hire must change in 12 months. What do you get?";
  return "Ask your CEO, CTO and lead investor separately what this hire must change in 12 months. What do you get?";
}

export function questionsForTrack(track: ArrGapTrack, hirerRole?: string): ArrGapQuestion[] {
  const source = track === "Senior PM" ? seniorPmQuestions : leaderQuestions;
  const roleAdjusted = source.map((question) => {
    if (track !== "Leader" || question.id !== "mandate") return question;
    const replacement = leaderMandateQuestion(hirerRole);
    if (typeof replacement === "string") return { ...question, prompt: replacement };
    return { ...question, prompt: replacement.prompt, answers: question.answers.map((answer, index) => index === 3 ? { ...answer, label: replacement.fourthAnswer } : answer) };
  });
  if (hirerRole !== "investor-talent-leader") return roleAdjusted;
  return roleAdjusted.map((question) => {
    const replacement = investorQuestionCopy[track][question.id];
    return { ...question, prompt: replacement.prompt, answers: question.answers.map((answer, index) => ({ ...answer, label: replacement.answers[index] ?? answer.label })) };
  });
}

export const arrGapContextQuestions: ArrGapContextQuestion[] = [
  { id: "arrBand", label: "ARR", options: [{ label: "Under £2m", value: "under-2m" }, { label: "£2-5m", value: "2m-to-5m" }, { label: "£5-10m", value: "5m-to-10m" }, { label: "£10-15m", value: "10m-to-15m" }, { label: "£15-20m", value: "15m-to-20m" }, { label: "£20m+", value: "20m-plus" }] },
  { id: "stage", label: "Funding stage", options: [{ label: "Seed", value: "seed" }, { label: "Series A", value: "series-a" }, { label: "Series B", value: "series-b" }, { label: "Growth", value: "growth" }, { label: "PE-backed", value: "pe-backed" }] },
  { id: "headcount", label: "Headcount", options: [{ label: "Under 40", value: "under-40" }, { label: "40-100", value: "40-to-100" }, { label: "100-250", value: "100-to-250" }, { label: "250+", value: "250-plus" }] },
  { id: "productTeamSize", label: "Product team today", options: [{ label: "None", value: "no-one" }, { label: "1 - 2", value: "one-to-three" }, { label: "3 - 4", value: "four-to-ten" }, { label: "5+", value: "ten-plus" }] },
  { id: "whoOwnsProduct", label: "Who owns product today", options: [{ label: "Founder or CEO", value: "founder-ceo" }, { label: "CTO", value: "cto" }, { label: "Head of Product", value: "head-of-product" }, { label: "VP or CPO", value: "vp-or-cpo" }] },
  { id: "growthGoal", label: "Growth goal, next 24 months", options: [{ label: "Double ARR", value: "double-arr" }, { label: "3x", value: "3x" }, { label: "4x or more", value: "4x-or-more" }, { label: "Protect and retain", value: "protect-retain" }] },
  { id: "mainLever", label: "Main lever you’re betting on", options: [{ label: "New product or AI", value: "new-product-ai" }, { label: "Expansion and retention", value: "expansion-retention" }, { label: "New segment or market", value: "new-segment-market" }, { label: "Pricing", value: "pricing" }] },
  { id: "hiringSituation", label: "Hiring situation", options: [{ label: "Live role now", value: "live-role-now" }, { label: "Hiring in 6-18 months", value: "hiring-6-to-18-months" }, { label: "Not sure what we need", value: "not-sure-what-we-need" }] },
  { id: "lean", label: "Level you’re leaning towards", options: [{ label: "Senior PM or Lead", value: "senior-pm-or-lead" }, { label: "Head of Product", value: "head-of-product" }, { label: "Director", value: "director" }, { label: "VP", value: "vp" }, { label: "CPO or CPTO", value: "cpo-or-cpto" }, { label: "No idea", value: "no-idea" }] },
  { id: "hardestPart", label: "Hardest part of hiring", options: [{ label: "Attracting good people to a risky company", value: "attracting-risk-company" }, { label: "Knowing who is actually good", value: "knowing-who-is-good" }, { label: "Affording them", value: "affording-them" }] },
  { id: "budgetFlexibility", label: "Budget flexibility", options: [{ label: "Fixed", value: "fixed" }, { label: "Can stretch about 15% for the right person", value: "stretch-15-percent" }, { label: "Not agreed with board", value: "not-agreed-with-board" }] },
];

const seniorPmOwnershipQuestion: ArrGapContextQuestion = {
  id: "seniorPmOwnership",
  label: "What will they own?",
  options: [
    { label: "One product area or squad", value: "one-area-or-squad" },
    { label: "Several squads or a platform", value: "several-squads-or-platform" },
    { label: "A strategic bet across the portfolio", value: "strategic-bet-across-portfolio" },
  ],
};

const seniorPmLeanQuestion: ArrGapContextQuestion = {
  id: "lean",
  label: "Level you’re leaning towards",
  options: [
    { label: "Senior PM", value: "senior-pm" },
    { label: "Lead PM", value: "lead-pm" },
    { label: "Principal PM", value: "principal-pm" },
    { label: "Head of Product", value: "head-of-product" },
    { label: "Director", value: "director" },
    { label: "VP", value: "vp" },
    { label: "CPO or CPTO", value: "cpo-or-cpto" },
    { label: "No idea", value: "no-idea" },
  ],
};

function investorContextQuestion(question: ArrGapContextQuestion): ArrGapContextQuestion {
  if (question.id === "seniorPmOwnership") return { ...question, label: "What will the company hire them to own?" };
  if (question.id === "mainLever") return { ...question, label: "Main lever the company is betting on" };
  if (question.id === "lean") return { ...question, label: "Level the CEO is leaning towards" };
  if (question.id === "hiringSituation") return { ...question, options: question.options.map((option) => option.value === "not-sure-what-we-need" ? { ...option, label: "The company is not sure what it needs" } : option) };
  return question;
}

export function contextQuestionsForTrack(track: ArrGapTrack, hirerRole?: string): ArrGapContextQuestion[] {
  const questions = track !== "Senior PM"
    ? arrGapContextQuestions
    : [seniorPmOwnershipQuestion, ...arrGapContextQuestions.map((question) => question.id === "lean" ? seniorPmLeanQuestion : question)];
  return hirerRole === "investor-talent-leader" ? questions.map(investorContextQuestion) : questions;
}

export type ArrGapAnswers = Partial<Record<ArrGapQuestionId, 1 | 2 | 3 | 4>>;
export type ArrGapContext = Partial<Record<ArrGapContextQuestionId, string>>;

export type ArrGapRisk = {
  id: ArrGapQuestionId;
  title: string;
  score: 1 | 2 | 3 | 4;
  riskCopy: string;
};

export type ArrGapReadinessBand =
  | "Several foundations are missing"
  | "Foundations are partly in place"
  | "Strong foundations";

export type ArrGapSuggestedLevel = "Senior PM" | "Lead PM" | "Principal PM" | "Head of Product" | "Director of Product" | "VP Product" | "CPO / CPTO";

export type LevelConfidence = "high" | "medium" | "low";
export type LevelReasonCode = "R0" | "R1" | "R2" | "R3" | "R4" | "R5" | "R6";

export type ArrGapLevelRecommendation = {
  level: ArrGapSuggestedLevel;
  confidence: LevelConfidence;
  reasonCode: LevelReasonCode;
  whyNot: string | null;
  unusual: boolean;
  note: string | null;
};

export type ArrGapLevelProfile = {
  hireThisWhen: string;
  whatTheyOwn: string;
  measuredOn: string;
  redFlag: string;
};

export type ArrGapResult = {
  readiness: number;
  totalScore: number;
  dimensions: ArrGapRisk[];
  risks: ArrGapRisk[];
  watchItem?: ArrGapRisk;
  firstFix?: ArrGapRisk;
  aiNotLever: boolean;
  readinessBand: ArrGapReadinessBand;
  suggestedLevel: ArrGapSuggestedLevel;
  levelRecommendation: ArrGapLevelRecommendation;
  levelProfile: ArrGapLevelProfile;
  recommendationReason?: string;
  hotLead: boolean;
};

const tieOrder: ArrGapQuestionId[] = ["decision-rights", "mandate", "commercial-ownership", "customer-evidence", "ai", "hiring-process"];

export function readinessBandFor(readiness: number): ArrGapReadinessBand {
  if (readiness <= 39) return "Several foundations are missing";
  if (readiness <= 69) return "Foundations are partly in place";
  return "Strong foundations";
}

export function scoreLabelFor(score: 1 | 2 | 3 | 4) {
  return ({ 1: "Missing", 2: "Gap", 3: "Solid", 4: "Strong" } as const)[score];
}

function arrUnder5m(value?: string) {
  return value === "under-2m" || value === "2m-to-5m";
}

function arrIs15mOrAbove(value?: string) {
  return value === "15m-to-20m" || value === "20m-plus";
}

function arrIsSeriesBOrAbove(value?: string) {
  return value === "series-b" || value === "growth" || value === "pe-backed";
}

function headcount100OrMore(value?: string) {
  return value === "100-to-250" || value === "250-plus";
}

function headcountUnder100(value?: string) {
  return value === "under-40" || value === "40-to-100";
}

export function trackFromContext(context: ArrGapContext): ArrGapTrack {
  return context.questionTrack === "Senior PM" ? "Senior PM" : "Leader";
}

const CTO_NOTE = "Product reporting into a technology leader is not a red flag in itself. The warning sign is product with no independent voice at the leadership table. Check that this role has one.";
const UNUSUAL_NOTE = "This combination is unusual: 250 or more people and no product team. Check it before you act. If it is right, a Head of Product is the starting point.";

const LEADERSHIP_LEVEL_PROFILES: Record<Exclude<ArrGapSuggestedLevel, "Senior PM" | "Lead PM" | "Principal PM">, ArrGapLevelProfile> = {
  "Head of Product": {
    hireThisWhen: "hire when the founder is still the product owner and the business needs its first product leader, or when you have PMs or product owners but no product leadership. Usually 40 to 150 people, one core product.",
    whatTheyOwn: "manage the squads directly, turn founder intuition into a repeatable product system without bureaucracy, still hands-on in the biggest bets.",
    measuredOn: "a prioritisation process the founder trusts enough to step back from, squad-level outcome metrics moving, PM quality rising.",
    redFlag: "the title with no founder access, no authority over priorities, or reporting into technology with no product decision rights.",
  },
  "Director of Product": {
    hireThisWhen: "hire when product has outgrown one leader's span: multiple PM leads, a second product line or business unit, or a platform and applications split. Usually 150 to 400 people, or a PE buy-and-build.",
    whatTheyOwn: "manage managers across a portfolio or business unit, own org design at that layer, allocate investment across teams, work with commercial leaders as a peer.",
    measuredOn: "portfolio or BU outcomes (NRR, gross margin, product-attributable growth) and the quality of the leads beneath them.",
    redFlag: "a Director hired into a single squad because the title needs to be senior.",
  },
  "VP Product": {
    hireThisWhen: "hire when there are multiple product teams and leaders and the CPO or founder cannot hold portfolio coordination alone. Typically £15m+ ARR, Series B/C or mid-cap PE.",
    whatTheyOwn: "build management capacity below the CPO or founder: org design, cross-portfolio sequencing, senior stakeholder alignment, succession for the leadership layer.",
    measuredOn: "multi-team throughput against strategy, portfolio alignment, and a bench of Directors and Heads who could step up.",
    redFlag: "hiring a VP to do a Head of Product's job because the title sounds more senior.",
  },
  "CPO / CPTO": {
    hireThisWhen: "hire when product is a company-level lever for growth, margin, retention, AI transformation or exit, and the CEO is genuinely ready to share ownership of what gets built.",
    whatTheyOwn: "product strategy, portfolio sequencing, senior leader development, turning AI from experiments into commercial capability, board-facing in PE.",
    measuredOn: "company-level growth, NRR and margin, and the product story in the exit equity narrative.",
    redFlag: "hiring a CPO without handing over real decision rights.",
  },
};

const PM_LEVEL_PROFILES: Record<Extract<ArrGapSuggestedLevel, "Senior PM" | "Lead PM" | "Principal PM">, ArrGapLevelProfile> = {
  "Senior PM": { hireThisWhen: "Senior PM owns a squad's outcomes end to end, usually the right first PM, measured on a commercial metric, red flag measuring on features shipped.", whatTheyOwn: "own a commercial outcome end to end.", measuredOn: "leading indicators they can move inside a quarter (activation, time-to-value, feature retention) tied to lagging revenue (expansion, churn, win rate).", redFlag: "measuring them on features shipped, which hires a delivery manager." },
  "Lead PM": { hireThisWhen: "Lead PM owns an area across two or three squads and sets direction for other PMs, red flag Lead as a retention title.", whatTheyOwn: "own a commercial outcome end to end.", measuredOn: "leading indicators they can move inside a quarter (activation, time-to-value, feature retention) tied to lagging revenue (expansion, churn, win rate).", redFlag: "measuring them on features shipped, which hires a delivery manager." },
  "Principal PM": { hireThisWhen: "Principal / Staff PM owns the company's biggest bet without direct reports, red flag hiring a Principal to avoid a management hire you need.", whatTheyOwn: "own a commercial outcome end to end.", measuredOn: "leading indicators they can move inside a quarter (activation, time-to-value, feature retention) tied to lagging revenue (expansion, churn, win rate).", redFlag: "measuring them on features shipped, which hires a delivery manager." },
};

export function levelProfileFor(level: ArrGapSuggestedLevel): ArrGapLevelProfile {
  return (PM_LEVEL_PROFILES[level as keyof typeof PM_LEVEL_PROFILES] ?? LEADERSHIP_LEVEL_PROFILES[level as keyof typeof LEADERSHIP_LEVEL_PROFILES]);
}

export type ArrGapLevelInputs = {
  track: ArrGapTrack | "PM" | "Not sure";
  arrBand?: string;
  stage?: string;
  headcount?: string;
  productTeam?: string;
  productTeamSize?: string;
  whoOwnsProduct?: string;
  lean?: string;
  decisionRights?: 1 | 2 | 3 | 4;
  mandate?: 1 | 2 | 3 | 4;
  whatTheyOwn?: string;
  seniorPmOwnership?: string;
};

function normaliseLevelValue(value?: string) {
  const values: Record<string, string> = {
    "Under 2m": "under-2m", "Under £2m": "under-2m", "2-5m": "2m-to-5m", "£2-5m": "2m-to-5m", "5-10m": "5m-to-10m", "£5-10m": "5m-to-10m", "10-15m": "10m-to-15m", "£10-15m": "10m-to-15m", "15-20m": "15m-to-20m", "£15-20m": "15m-to-20m", "20m+": "20m-plus", "£20m+": "20m-plus",
    Seed: "seed", "Series A": "series-a", "Series B": "series-b", Growth: "growth", "PE-backed": "pe-backed",
    "Under 40": "under-40", "40-100": "40-to-100", "100-250": "100-to-250", "250+": "250-plus",
    none: "no-one", "No one": "no-one", None: "no-one", "1-3": "one-to-three", "1 - 2": "one-to-three", "1-2": "one-to-three", "4-10": "four-to-ten", "3 - 4": "four-to-ten", "3-4": "four-to-ten", "10+": "ten-plus", "5+": "ten-plus",
    "Founder or CEO": "founder-ceo", "Founder/CEO": "founder-ceo", CTO: "cto", "Head of Product": "head-of-product", VP: "vp", CPO: "cpo", "VP or CPO": "vp-or-cpo", "VP or CPO (VP)": "vp", "VP or CPO (CPO)": "cpo",
    "No idea": "no-idea", "Senior PM": "senior-pm", "Senior PM or Lead": "senior-pm-or-lead", "Lead PM": "lead-pm", "Principal PM": "principal-pm", Director: "director", "CPO or CPTO": "cpo-or-cpto",
    "one squad": "one-area-or-squad", "One product area or squad": "one-area-or-squad", "several squads or a platform": "several-squads-or-platform", "Several squads or a platform": "several-squads-or-platform", "a strategic bet": "strategic-bet-across-portfolio", "A strategic bet across the portfolio": "strategic-bet-across-portfolio",
  };
  return values[value ?? ""] ?? value;
}

function normaliseLevelInputs(input: ArrGapLevelInputs): ArrGapLevelInputs {
  return {
    ...input,
    arrBand: normaliseLevelValue(input.arrBand),
    stage: normaliseLevelValue(input.stage),
    headcount: normaliseLevelValue(input.headcount),
    productTeamSize: normaliseLevelValue(input.productTeam ?? input.productTeamSize),
    whoOwnsProduct: normaliseLevelValue(input.whoOwnsProduct),
    lean: normaliseLevelValue(input.lean),
    seniorPmOwnership: normaliseLevelValue(input.whatTheyOwn ?? input.seniorPmOwnership),
  };
}

function leanLabel(value?: string) {
  return ({ "senior-pm-or-lead": "Senior PM or Lead", "senior-pm": "Senior PM", "lead-pm": "Lead PM", "principal-pm": "Principal PM", "head-of-product": "Head of Product", director: "Director", vp: "VP", "cpo-or-cpto": "CPO or CPTO" } as Record<string, string>)[value ?? ""] ?? "";
}

function leanMatchesLevel(lean: string | undefined, level: ArrGapSuggestedLevel) {
  const matches: Record<string, ArrGapSuggestedLevel[]> = {
    "senior-pm-or-lead": ["Senior PM", "Lead PM"], "senior-pm": ["Senior PM"], "lead-pm": ["Lead PM"], "principal-pm": ["Principal PM"], "head-of-product": ["Head of Product"], director: ["Director of Product"], vp: ["VP Product"], "cpo-or-cpto": ["CPO / CPTO"],
  };
  return matches[lean ?? ""]?.includes(level) ?? false;
}

function levelRank(level: ArrGapSuggestedLevel) {
  return ({ "Senior PM": 1, "Lead PM": 2, "Principal PM": 3, "Head of Product": 4, "Director of Product": 5, "VP Product": 6, "CPO / CPTO": 7 } as Record<ArrGapSuggestedLevel, number>)[level];
}

function leanRank(lean?: string) {
  return ({ "senior-pm-or-lead": 1, "senior-pm": 1, "lead-pm": 2, "principal-pm": 3, "head-of-product": 4, director: 5, vp: 6, "cpo-or-cpto": 7 } as Record<string, number>)[lean ?? ""] ?? 0;
}

function cpoGatePasses(input: ArrGapLevelInputs) {
  return arrIs15mOrAbove(input.arrBand) && (input.stage === "growth" || input.stage === "pe-backed") && input.decisionRights === 4 && (input.mandate === 3 || input.mandate === 4);
}

function whyNotFor(input: ArrGapLevelInputs, level: ArrGapSuggestedLevel, isFirstPm: boolean): string | null {
  const lean = input.lean;
  if (!lean || lean === "no-idea" || leanMatchesLevel(lean, level)) return null;
  const leanText = leanLabel(lean);
  if (lean === "cpo-or-cpto" && levelRank(level) < levelRank("CPO / CPTO")) {
    const drSentence = input.decisionRights === 4 ? "Your answer says you have already named and handed over the decisions, which is the strongest part of your case." : input.decisionRights === 3 ? "Your answer says you have handed over some decisions but not all of them." : "Your answer says you have not yet let go of the decisions that make the role strategic.";
    return `You were leaning towards a CPO or CPTO. On your answers a ${level} is the safer hire now, for three reasons. First, a CPO only works when the CEO shares real decision rights. ${drSentence} Second, a CPO's impact takes 12 to 24 months to show in the numbers, while boards and founders usually judge at 6 to 9. Third, average CPO tenure is 2.6 years (survey of 1,000 CPOs). If you can name three decisions you will stop making on day one, and your stage supports it, revisit the CPO question.`;
  }
  if (lean === "vp" && levelRank(level) < levelRank("VP Product")) return "A VP builds management capacity below a CPO or founder. You do not yet have several product leaders to coordinate, so a VP would be doing a Head of Product's job under a more senior title.";
  if (lean === "director" && levelRank(level) < levelRank("Director of Product")) return "A Director manages managers across a portfolio. Without several PM leads or a second product line, a Director either manages a layer too deep or leaves inside a year.";
  if (lean === "head-of-product" && level === "Senior PM" && isFirstPm) return "At your size the first product hire should be a senior person who wants to build, not a Head of Product who wants a team. The signal to hire a leader is your calendar: when prioritising and specs take more of your time than selling and raising.";
  if (leanRank(lean) < levelRank(level)) {
    const ownerPhrase = input.whoOwnsProduct === "cto" ? "product is still being decided inside technology" : "the founder is still the prioritiser";
    return `You were leaning towards a ${leanText}. Your answers describe a leadership problem: ${ownerPhrase}. A ${leanText} would own a squad, not the system around it.`;
  }
  return `You were leaning towards a ${leanText}. Your answers point to a ${level}. The level table says a ${level} fits when ${levelProfileFor(level).hireThisWhen}`;
}

export function getLevel(input: ArrGapLevelInputs): ArrGapLevelRecommendation {
  input = normaliseLevelInputs(input);
  const isPmTrack = input.track === "Senior PM" || input.track === "PM";
  let level: ArrGapSuggestedLevel;
  let reasonCode: LevelReasonCode;
  let unusual = false;
  let note: string | null = null;
  let isFirstPm = false;

  if (isPmTrack) {
    level = input.seniorPmOwnership === "several-squads-or-platform" ? "Lead PM" : input.seniorPmOwnership === "strategic-bet-across-portfolio" ? "Principal PM" : "Senior PM";
    reasonCode = "R0";
  } else if (input.productTeamSize === "no-one" && input.headcount === "under-40" && arrUnder5m(input.arrBand)) {
    level = "Head of Product";
    reasonCode = "R1";
    note = "This is the first product hire. The founder is the product owner today, so the Head of Product should build the product function from here.";
  } else if ((input.whoOwnsProduct === "founder-ceo" || input.whoOwnsProduct === "cto") && headcountUnder100(input.headcount)) {
    level = "Head of Product";
    reasonCode = "R2";
    note = input.whoOwnsProduct === "cto" ? CTO_NOTE : "Confirm this level in the workshop.";
  } else if (
    (input.productTeamSize === "ten-plus") &&
    (input.decisionRights === 3 || input.decisionRights === 4) &&
    (arrIs15mOrAbove(input.arrBand) || input.stage === "pe-backed") &&
    input.whoOwnsProduct !== "vp" && input.whoOwnsProduct !== "vp-or-cpo" && input.whoOwnsProduct !== "cpo"
  ) {
    level = "CPO / CPTO";
    reasonCode = "R3";
  } else if (
    input.productTeamSize === "ten-plus" &&
    (input.whoOwnsProduct === "head-of-product" || input.whoOwnsProduct === "vp" || input.whoOwnsProduct === "vp-or-cpo" || input.whoOwnsProduct === "cpo") &&
    (arrIs15mOrAbove(input.arrBand) || arrIsSeriesBOrAbove(input.stage))
  ) {
    level = "VP Product";
    reasonCode = "R4";
    if ((input.decisionRights === 1 || input.decisionRights === 2) && input.whoOwnsProduct === "head-of-product") {
      note = "CPO: not yet. If you can't name three decisions you'll stop making on day one, hire a VP and revisit.";
    }
  } else if ((input.productTeamSize === "four-to-ten" || input.productTeamSize === "ten-plus") && headcount100OrMore(input.headcount)) {
    level = "Director of Product";
    reasonCode = "R5";
  } else {
    level = "Head of Product";
    reasonCode = "R6";
    note = "Confirm this level in the workshop.";
  }

  const confidence: LevelConfidence = unusual ? "low" : reasonCode === "R0" ? "high" : level === "CPO / CPTO" ? "medium" : (input.lean === "no-idea" || leanMatchesLevel(input.lean, level)) ? "high" : "medium";
  return { level, confidence, reasonCode, whyNot: whyNotFor(input, level, isFirstPm), unusual, note };
}

function levelInputsFrom(answers: ArrGapAnswers, context: ArrGapContext, track = trackFromContext(context)): ArrGapLevelInputs {
  return { track, arrBand: context.arrBand, stage: context.stage, headcount: context.headcount, productTeamSize: context.productTeamSize, whoOwnsProduct: context.whoOwnsProduct, lean: context.lean, decisionRights: answers["decision-rights"], mandate: answers.mandate, seniorPmOwnership: context.seniorPmOwnership };
}

export function suggestedArrGapLevel(answers: ArrGapAnswers, context: ArrGapContext, track = trackFromContext(context)): ArrGapSuggestedLevel {
  return getLevel(levelInputsFrom(answers, context, track)).level;
}

export function arrGapRecommendationReason(answers: ArrGapAnswers, context: ArrGapContext, level: ArrGapSuggestedLevel, track = trackFromContext(context)) {
  const recommendation = getLevel(levelInputsFrom(answers, context, track));
  return recommendation.note ?? levelProfileFor(level).hireThisWhen;
}

export function calculateArrGapResult(answers: ArrGapAnswers, context: ArrGapContext, track = trackFromContext(context)): ArrGapResult | null {
  const questions = questionsForTrack(track, context.hirerRole);
  if (!questions.every((question) => answers[question.id])) return null;
  const totalScore = questions.reduce((total, question) => total + (answers[question.id] ?? 0), 0);
  const dimensions = questions.map((question) => ({
    id: question.id,
    title: question.label,
    score: answers[question.id] as 1 | 2 | 3 | 4,
    riskCopy: question.riskCopy,
  }));
  const ranked = [...dimensions].sort((a, b) => a.score - b.score || tieOrder.indexOf(a.id) - tieOrder.indexOf(b.id));
  const risks = ranked.filter((dimension) => dimension.score <= 2).slice(0, 2);
  const watchItem = risks.length < 2 ? ranked.find((dimension) => dimension.score === 3) : undefined;
  const readiness = Math.round(((totalScore - 6) / 18) * 100);
  const levelRecommendation = getLevel(levelInputsFrom(answers, context, track));
  const suggestedLevel = levelRecommendation.level;
  return {
    totalScore,
    readiness,
    readinessBand: readinessBandFor(readiness),
    dimensions,
    risks,
    watchItem,
    firstFix: risks[0],
    aiNotLever: (answers.ai === 1 || answers.ai === 2) && context.mainLever !== "new-product-ai",
    suggestedLevel,
    levelRecommendation,
    levelProfile: levelProfileFor(suggestedLevel),
    recommendationReason: arrGapRecommendationReason(answers, context, suggestedLevel, track),
    hotLead: context.hiringSituation === "live-role-now" && totalScore < 17 && (context.growthGoal === "3x" || context.growthGoal === "4x-or-more"),
  };
}

export function displayContextValue(questionId: ArrGapContextQuestionId, value?: string) {
  const question = [...arrGapContextQuestions, seniorPmOwnershipQuestion, seniorPmLeanQuestion].find((item) => item.id === questionId && item.options.some((option) => option.value === value));
  return question?.options.find((option) => option.value === value)?.label ?? "";
}

export function shuffledAnswers(answers: ArrGapAnswer[]) {
  const copied = [...answers];
  for (let index = copied.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [copied[index], copied[swapIndex]] = [copied[swapIndex], copied[index]];
  }
  return copied;
}
